/**
 * SpeechEngine - Abstract interface for speech recognition engines
 * 
 * This provides a provider-agnostic interface for speech recognition,
 * allowing easy swapping between different STT providers and testing.
 */

export class SpeechEngine {
    constructor() {
        this.onPartial = null;
        this.onFinal = null;
        this.onError = null;
        this.onStateChange = null;
    }

    /**
     * Start speech recognition
     * @returns {Promise<boolean>} Success status
     */
    async start() {
        throw new Error('start() must be implemented by subclass');
    }

    /**
     * Stop speech recognition
     * @returns {Promise<boolean>} Success status
     */
    async stop() {
        throw new Error('stop() must be implemented by subclass');
    }

    /**
     * Check if currently listening
     * @returns {boolean} Listening status
     */
    isListening() {
        throw new Error('isListening() must be implemented by subclass');
    }

    /**
     * Get current state
     * @returns {string} Current state
     */
    getState() {
        throw new Error('getState() must be implemented by subclass');
    }

    /**
     * Set callback for partial results
     * @param {Function} callback - Callback function
     */
    setOnPartial(callback) {
        this.onPartial = callback;
    }

    /**
     * Set callback for final results
     * @param {Function} callback - Callback function
     */
    setOnFinal(callback) {
        this.onFinal = callback;
    }

    /**
     * Set callback for errors
     * @param {Function} callback - Callback function
     */
    setOnError(callback) {
        this.onError = callback;
    }

    /**
     * Set callback for state changes
     * @param {Function} callback - Callback function
     */
    setOnStateChange(callback) {
        this.onStateChange = callback;
    }

    /**
     * Emit an event to registered callbacks
     * @param {string} event - Event name
     * @param {*} data - Event data
     */
    emit(event, data) {
        switch (event) {
            case SpeechEvents.PARTIAL_RESULT:
                if (this.onPartial) {
                    this.onPartial(data);
                }
                break;
            case SpeechEvents.FINAL_RESULT:
                if (this.onFinal) {
                    this.onFinal(data);
                }
                break;
            case SpeechEvents.ERROR:
                if (this.onError) {
                    this.onError(data);
                }
                break;
            case SpeechEvents.STATE_CHANGE:
                if (this.onStateChange) {
                    this.onStateChange(data);
                }
                break;
            default:
                // Handle custom events like 'vad_unavailable'
                if (this.onError) {
                    this.onError({ type: event, data });
                }
                break;
        }
    }

    /**
     * Cleanup resources
     */
    destroy() {
        this.onPartial = null;
        this.onFinal = null;
        this.onError = null;
        this.onStateChange = null;
    }
}

/**
 * Speech Recognition States
 */
export const SpeechStates = {
    IDLE: 'idle',
    ACQUIRING_MIC: 'acquiring_mic',
    LISTENING: 'listening',
    ENDPOINTED: 'endpointed',
    ERROR: 'error'
};

/**
 * Speech Recognition Events
 */
export const SpeechEvents = {
    PARTIAL_RESULT: 'partial_result',
    FINAL_RESULT: 'final_result',
    ERROR: 'error',
    STATE_CHANGE: 'state_change',
    VAD_UNAVAILABLE: 'vad_unavailable',
    STREAMING_FALLBACK_NEEDED: 'streaming_fallback_needed'
};
